import { cn } from "@/lib/cn";
import { type Change, diffLines } from "diff";

interface BodyDiffProps {
  oldBody: string;
  newBody: string;
}

export function BodyDiff({ oldBody, newBody }: BodyDiffProps) {
  if (oldBody === newBody) {
    return <p className="text-sm italic text-zinc-500">No changes</p>;
  }

  const changes: Change[] = diffLines(oldBody, newBody);

  return (
    <div
      className={cn(
        "rounded border border-zinc-300 dark:border-zinc-700",
        "bg-zinc-50 dark:bg-zinc-900",
        "p-4 overflow-x-auto",
      )}
    >
      <div className="font-mono text-xs leading-relaxed">
        {changes.map((change, idx) => {
          const type = change.added ? "added" : change.removed ? "removed" : "context";
          const prefix = change.added ? "+" : change.removed ? "-" : " ";
          const bgClass = change.added
            ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200"
            : change.removed
              ? "bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200"
              : "text-zinc-700 dark:text-zinc-300";

          // Split by lines but keep structure
          const lines = change.value.split("\n");
          // Remove trailing empty line if exists
          if (lines[lines.length - 1] === "") {
            lines.pop();
          }

          return lines.map((line, lineIdx) => {
            const lineKey = `${type}-${idx}-${lineIdx}`;
            return (
              <div key={lineKey} data-diff={type} className={cn("whitespace-pre-wrap", bgClass)}>
                <span className="select-none opacity-50">{prefix}</span>
                {line}
              </div>
            );
          });
        })}
      </div>
    </div>
  );
}

interface ConfigDiffProps {
  oldConfig: Record<string, unknown> | null;
  newConfig: Record<string, unknown> | null;
}

export function ConfigDiff({ oldConfig, newConfig }: ConfigDiffProps) {
  const oldKeys = new Set(Object.keys(oldConfig ?? {}));
  const newKeys = new Set(Object.keys(newConfig ?? {}));
  const allKeys = Array.from(new Set([...oldKeys, ...newKeys])).sort();

  return (
    <div className="space-y-1">
      {allKeys.map((key) => {
        const inOld = oldKeys.has(key);
        const inNew = newKeys.has(key);
        const oldVal = oldConfig?.[key];
        const newVal = newConfig?.[key];

        let status: "added" | "removed" | "changed" | "unchanged";
        let bgClass: string;

        if (!inOld && inNew) {
          status = "added";
          bgClass =
            "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700";
        } else if (inOld && !inNew) {
          status = "removed";
          bgClass =
            "bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200 border-rose-300 dark:border-rose-700";
        } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          status = "changed";
          bgClass =
            "bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700";
        } else {
          status = "unchanged";
          bgClass =
            "bg-zinc-50 dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-700";
        }

        return (
          <div
            key={key}
            className={cn(
              "px-3 py-2 rounded border",
              "font-mono text-xs",
              "flex items-center gap-2",
              bgClass,
            )}
          >
            <span className="font-semibold">{key}:</span>
            {status === "changed" ? (
              <span>
                {JSON.stringify(oldVal)} → {JSON.stringify(newVal)}
              </span>
            ) : (
              <span>{JSON.stringify(inNew ? newVal : oldVal)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface TagsDiffProps {
  oldTags: string[];
  newTags: string[];
}

export function TagsDiff({ oldTags, newTags }: TagsDiffProps) {
  const oldSet = new Set(oldTags);
  const newSet = new Set(newTags);

  const added = newTags.filter((t) => !oldSet.has(t));
  const removed = oldTags.filter((t) => !newSet.has(t));
  const unchanged = oldTags.filter((t) => newSet.has(t));

  return (
    <div className="flex flex-wrap gap-2">
      {added.map((tag) => (
        <span
          key={`added-${tag}`}
          className={cn(
            "inline-flex items-center",
            "px-2 py-1 rounded",
            "border border-emerald-400 dark:border-emerald-600",
            "bg-emerald-50 dark:bg-emerald-950/30",
            "font-mono text-xs",
            "text-emerald-800 dark:text-emerald-200",
          )}
        >
          {tag}
        </span>
      ))}
      {removed.map((tag) => (
        <span
          key={`removed-${tag}`}
          className={cn(
            "inline-flex items-center",
            "px-2 py-1 rounded",
            "border border-rose-400 dark:border-rose-600",
            "bg-rose-50 dark:bg-rose-950/30",
            "font-mono text-xs line-through",
            "text-rose-800 dark:text-rose-200",
          )}
        >
          {tag}
        </span>
      ))}
      {unchanged.map((tag) => (
        <span
          key={`unchanged-${tag}`}
          className={cn(
            "inline-flex items-center",
            "px-2 py-1 rounded",
            "border border-zinc-300 dark:border-zinc-700",
            "bg-zinc-50 dark:bg-zinc-900",
            "font-mono text-xs",
            "text-zinc-700 dark:text-zinc-300",
          )}
        >
          {tag}
        </span>
      ))}
    </div>
  );
}
